import axios from "axios";
import type {
  Adjustment,
  AdjustmentDetail,
  AdjustmentListQuery,
  AdjustmentRaiser,
  Approver,
  QueueItem,
  RaisableReason,
  RaiseAdjustmentPayload,
} from "@/interfaces/adjustments";
import { apiClient } from "@/lib/apiClient";

/** Rethrows the server's error message (not a bare axios error) so the UI can show it. */
function rethrowApiError(error: unknown, fallback: string): never {
  if (axios.isAxiosError(error) && error.response?.data?.error) {
    throw new Error(error.response.data.error as string);
  }
  throw error instanceof Error ? error : new Error(fallback);
}

const base = "/admin/adjustments";
// The queue reviews every action type, not just adjustments, so it has its own mount.
const approvals = "/admin/approvals";

export interface AdjustmentListResponse {
  data: Adjustment[];
  total: number;
  page: number;
  totalPages: number;
}

export async function fetchAdjustments(
  q: AdjustmentListQuery = {},
): Promise<AdjustmentListResponse> {
  const { data } = await apiClient.get<{ success: boolean } & AdjustmentListResponse>(base, {
    params: q,
  });
  return { data: data.data, total: data.total, page: data.page, totalPages: data.totalPages };
}

export async function fetchAdjustment(id: number): Promise<AdjustmentDetail> {
  const { data } = await apiClient.get<{ success: boolean; data: AdjustmentDetail }>(
    `${base}/${id}`,
  );
  return data.data;
}

export async function fetchAdjustmentRaisers(): Promise<AdjustmentRaiser[]> {
  const { data } = await apiClient.get<{ success: boolean; data: AdjustmentRaiser[] }>(
    `${base}/raisers`,
  );
  return data.data;
}

export async function fetchRaisableReasons(): Promise<RaisableReason[]> {
  const { data } = await apiClient.get<{ success: boolean; data: RaisableReason[] }>(
    `${base}/reasons`,
  );
  return data.data;
}

export async function raiseAdjustment(payload: RaiseAdjustmentPayload) {
  try {
    const { data } = await apiClient.post<{ success: boolean; data: unknown }>(base, payload);
    return data.data;
  } catch (err) {
    return rethrowApiError(err, "Could not raise the adjustment");
  }
}

export async function fetchApprovalQueue(): Promise<QueueItem[]> {
  const { data } = await apiClient.get<{ success: boolean; data: QueueItem[] }>(
    `${approvals}/queue`,
  );
  return data.data;
}

export async function decideApproval(id: number, approve: boolean, note?: string) {
  try {
    const { data } = await apiClient.post<{ success: boolean; data: unknown }>(
      `${approvals}/queue/${id}/decide`,
      { approve, note },
    );
    return data.data;
  } catch (err) {
    return rethrowApiError(err, approve ? "Could not approve" : "Could not reject");
  }
}

export async function cancelApproval(id: number) {
  try {
    const { data } = await apiClient.post<{ success: boolean; data: unknown }>(
      `${approvals}/queue/${id}/cancel`,
      {},
    );
    return data.data;
  } catch (err) {
    return rethrowApiError(err, "Could not cancel the request");
  }
}

export async function fetchApprovers(): Promise<Approver[]> {
  const { data } = await apiClient.get<{ success: boolean; data: Approver[] }>(
    `${approvals}/approvers`,
  );
  return data.data;
}

export async function addApprover(userId: number) {
  try {
    const { data } = await apiClient.post<{ success: boolean; data: Approver }>(
      `${approvals}/approvers`,
      { userId },
    );
    return data.data;
  } catch (err) {
    return rethrowApiError(err, "Could not add that approver");
  }
}

export async function removeApprover(userId: number) {
  try {
    await apiClient.delete(`${approvals}/approvers/${userId}`);
  } catch (err) {
    return rethrowApiError(err, "Could not stand that approver down");
  }
}
